import { Button } from "@/components/Buttons";
import ErrorMessage from "@/components/Form/Parts/ErrorMessage";
import { HiddenField } from "@/components/Form/Parts/index";
import { usePaymentMethodsQuery, useRecaptchaV3 } from "@/hooks";
import { PAYMENT_METHOD_ID, errorMessages } from "@/lib/constants";
import routes from "@/lib/routes";
import yupCreditCardNameValidate from "@/lib/yupCreditCardNameValidate";
import yupCreditCardNumberValidate from "@/lib/yupCreditCardNumberValidate";
import yupExpirationDateValidate from "@/lib/yupExpirationDateValidate";
import formStyles from "@/styles/Form.module.scss";
import paymentSelectStyles from "@/styles/PaymentSelect.module.scss";
import processStyles from "@/styles/Process.module.scss";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as y from "yup";
import CreditCardInput from "./CreditCardInput";
import PaymentInstruments from "./PaymentInstruments";
import PaymentMethods from "./PaymentMethods";
import RecaptchaV2 from "./RecaptchaV2";

yupExpirationDateValidate(y);
yupCreditCardNumberValidate(y);
yupCreditCardNameValidate(y);

export const CREATE_ONLY_MODE = ["addpayment"];
export const PURCHASE_MODE = ["svod", "tvod"];
export const IGNORE_PAYPAY = ["svod", "changesvod"];
export const IGNORE_CARRIER = ["tvod", "svod", "changesvod", "addpayment"];

export default function PaymentSelectForm({
  mode,
  onSubmit,
  paymentText,
  registerPaymentText,
  error,
  clearError,
  recaptchaSuccessV3,
  recaptchaCounterV2,
}) {
  const [disableSubmitWithRecaptcha, setDisableSubmitWithRecaptcha] =
    useState(false);
  const [recaptchaTokenV2, setRecaptchaTokenV2] = useState();

  const { showRecaptchaV3Badge, hideRecaptchaV3Badge } = useRecaptchaV3();
  // recaptchaバッジの表示制御
  useEffect(() => {
    showRecaptchaV3Badge();
    return () => {
      hideRecaptchaV3Badge();
    };
  }, []);

  const [state, setState] = useState({
    showInput: false,
    ccInputError: false,
    paymentMethod: null,
    paymentInstrument: null,
    createPayment: CREATE_ONLY_MODE.includes(mode),
  });
  const { showInput, paymentMethod, createPayment } = state;

  useEffect(() => {
    // 支払い方法にクレジットカード選択かつ、reCAPTCHAv3が失敗したかつ、recaptchaTokenV2が未生成の場合は送信ボタンを無効化
    if (showInput && recaptchaSuccessV3 === false && !recaptchaTokenV2) {
      setDisableSubmitWithRecaptcha(true);
    } else {
      setDisableSubmitWithRecaptcha(false);
    }
  }, [recaptchaTokenV2, recaptchaSuccessV3, showInput]);

  const schema = useMemo(() => {
    const withCreditCardSchema = {
      cardNumber: y
        .string()
        .creditCardNumber()
        .required("カード番号を入力してください。"),
      expirationDate: y
        .string()
        .min(6, "有効期限を選択してください。")
        .expirationDate()
        .required("有効期限を選択してください。"),
      cvv: y
        .string()
        .min(3, "3桁または4桁で入力してください。")
        .max(4, "3桁または4桁で入力してください。")
        .required("セキュリティコードを入力してください。"),
      firstName: y
        .string()
        .firstNameCheck()
        .required("カード名義（英字名）を入力してください。"),
      lastName: y
        .string()
        .lastNameCheck()
        .required("カード名義（英字姓）を入力してください。"),
      mailAddress: y
        .string()
        .email("メールアドレスの形式で入力してください。")
        .required("メールアドレスが入力されていません。"),
      creditCardTerm: y
        .boolean()
        .required("規約に同意してください。")
        .oneOf([true], "規約に同意してください。"),
    };

    let currentSchema = {
      createPayment: y.boolean(),
    };

    if (createPayment) {
      currentSchema.paymentMethod = y
        .number()
        .typeError("選択してください。")
        .required("選択してください。");
      currentSchema.paymentInstrument = y.mixed().nullable();

      if (
        paymentMethod === PAYMENT_METHOD_ID.CREDIT_CARD_3DS ||
        paymentMethod === PAYMENT_METHOD_ID.CREDIT_CARD_NO3DS
      ) {
        return y.object().shape({ ...currentSchema, ...withCreditCardSchema });
      }
    } else {
      currentSchema.paymentInstrument = y
        .number()
        .typeError("選択してください。")
        .required("選択してください。");
      currentSchema.paymentMethod = y.mixed().nullable();
    }
    return y.object().shape(currentSchema);
  }, [createPayment, paymentMethod]);

  const { register, handleSubmit, formState, setValue, trigger, watch } =
    useForm({
      resolver: yupResolver(schema),
      mode: "all",
      shouldUnregister: true,
    });

  const watchedCardNumber = watch("cardNumber");

  useEffect(() => {
    register("recaptchaType");
    register("recaptchaToken");
  }, []);

  useEffect(() => {
    if (recaptchaSuccessV3 === true) {
      setValue("recaptchaType", "v3");
    } else if (recaptchaSuccessV3 === false) {
      setValue("recaptchaType", "v2");
    } else {
      setValue("recaptchaType", "v3");
    }
  }, [recaptchaSuccessV3]);

  const { isDirty, isValid, isSubmitting, errors } = formState;

  const { data, loading } = usePaymentMethodsQuery();
  const paymentMethods = data?.paymentMethods;
  const paymentInstrumentData = data?.paymentInstruments;

  const onPaymentMethodChange = useCallback(
    async (newState) => {
      setState((prevState) => ({ ...prevState, ...newState }));

      if (newState.createPayment) {
        setValue("paymentInstrument", null, { shouldValidate: true });
        setValue("createPayment", true);
      } else {
        setValue("paymentMethod", newState.paymentMethod, {
          shouldValidate: true,
        });
        setValue("createPayment", false);
      }

      if (clearError) clearError();

      if (!newState.showInput) {
        setTimeout(() => {
          trigger();
        }, 50);
      }
    },
    [clearError, setValue, trigger]
  );

  if (loading) return null;

  const isCardModeButEmpty = showInput && !watchedCardNumber;
  const isFormInvalid = !isValid || isCardModeButEmpty;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={processStyles.contentWrapper}>
        <div className={processStyles.content}>
          <h3
            className={`${processStyles.contentHeader} ${processStyles.required}`}
          >
            お支払い方法
          </h3>
          <PaymentInstruments
            mode={mode}
            paymentInstruments={paymentInstrumentData}
            register={register}
            onChange={onPaymentMethodChange}
            setValue={setValue}
          />
          <PaymentMethods
            mode={mode}
            paymentMethods={paymentMethods}
            paymentInstruments={paymentInstrumentData}
            register={register}
            onChange={onPaymentMethodChange}
            setValue={setValue}
          />
          {showInput && (
            <CreditCardInput
              register={register}
              errors={errors}
              setValue={setValue}
              visible={true}
            />
          )}
          <HiddenField {...register("createPayment")} value={createPayment} />
          {error && error !== errorMessages["recaptcha"] && (
            <div className={paymentSelectStyles.error}>
              <ErrorMessage msg={error} />
            </div>
          )}
        </div>
      </div>
      {PURCHASE_MODE.includes(mode) && (
        <div className={formStyles.guideArea}>
          <p>
            ※特定商取引法について
            <Link
              href={routes.legalSctLaw}
              target="_blank"
              rel="noopener noreferrer"
              className={formStyles.textLink}
            >
              こちら
            </Link>
            からご確認ください。
          </p>
        </div>
      )}

      <div className={formStyles.btnArea}>
        {error && error === errorMessages["recaptcha"] && !recaptchaTokenV2 && (
          <div className={paymentSelectStyles.error}>
            <ErrorMessage msg={error} />
          </div>
        )}
        {recaptchaSuccessV3 === false && (
          <RecaptchaV2
            visible={showInput}
            setValue={setValue}
            setRecaptchaTokenV2={setRecaptchaTokenV2}
            recaptchaCounterV2={recaptchaCounterV2}
          ></RecaptchaV2>
        )}
        <Button
          type="submit"
          fillColor
          large
          fontFixed
          disabled={isFormInvalid || disableSubmitWithRecaptcha}
          loading={isSubmitting}
        >
          {!createPayment && <>{paymentText}</>}
          {createPayment && <>{registerPaymentText || paymentText}</>}
        </Button>
      </div>
    </form>
  );
}
